/**
 * usePinnedJobs.js — Per-user job pinning
 *
 * Reads pinned job IDs from the auth profile (pinned_jobs column on profiles table).
 * Provides togglePin(jobId) to add/remove pins, with optimistic local state.
 */
import { useCallback, useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { api } from '../lib/api';

export function usePinnedJobs() {
  const { profile, refreshProfile } = useAuth();
  const userId = profile?.id;

  // Local copy for optimistic updates
  const [pinnedIds, setPinnedIds] = useState(() => profile?.pinned_jobs || []);

  // Sync when profile loads/changes
  useEffect(() => {
    setPinnedIds(profile?.pinned_jobs || []);
  }, [profile?.pinned_jobs]);

  const isPinned = useCallback((jobId) => pinnedIds.includes(jobId), [pinnedIds]);

  const togglePin = useCallback(async (jobId) => {
    if (!userId) return;

    const wasPinned = pinnedIds.includes(jobId);

    // Optimistic update — functional form so rapid toggles on different jobs
    // don't clobber each other via a stale closure.
    setPinnedIds((cur) =>
      cur.includes(jobId) ? cur.filter((id) => id !== jobId) : [...cur, jobId]
    );

    try {
      // Atomic single-id add/remove server-side — never sends the whole array,
      // so a second tab/device can't overwrite pins it hasn't seen.
      const res = await api.put(`/profiles/${userId}/pins`, { jobId, pinned: !wasPinned });
      if (Array.isArray(res?.pinned_jobs)) setPinnedIds(res.pinned_jobs);
      // Keep the auth profile in sync for other views / this hook on remount.
      refreshProfile();
    } catch (e) {
      // Reconcile from the server on failure.
      await refreshProfile();
      console.error('Failed to update pinned jobs:', e);
    }
  }, [userId, pinnedIds, refreshProfile]);

  return { pinnedIds, isPinned, togglePin };
}
